"use client";

import { useEffect, useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "motion/react";
import { useTranslation } from "@/lib/i18n/context";

/**
 * Archive section — left column carries the editorial copy, right column
 * auto-cycles through a stack of real demo entries (Alex Rivera, CEO of
 * Lumea Health). Every ~4.5 s the active entry rotates: top fades out,
 * middle promotes, bottom shifts up, a fresh entry slides in at the back.
 */
export function ArchiveSection() {
  const { t } = useTranslation();
  const a = t.landing.cover.archive;
  const bridge = t.landing.cover.pricing.bridge;
  const reduced = useReducedMotion();
  const entries = a.entries;
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    if (reduced || entries.length === 0) return;
    const id = setInterval(() => {
      setActiveIndex((i) => (i + 1) % entries.length);
    }, 4500);
    return () => clearInterval(id);
  }, [reduced, entries.length]);

  const at = (offset: number) => entries[(activeIndex + offset) % entries.length];

  return (
    <section className="relative max-w-[1320px] mx-auto px-6 md:px-12 py-24 md:py-[120px] z-[2]">
      <div className="grid md:grid-cols-[1fr_1.05fr] gap-14 md:gap-20 items-center">
        <div className="max-w-[520px]">
          <div className="kicker-mono inline-flex items-center gap-2.5 mb-6">
            <span className="w-1.5 h-1.5 rounded-full bg-[var(--terracotta)]" />
            <span>{a.kicker}</span>
          </div>
          <h2
            className="font-display text-[clamp(38px,4.2vw,58px)] leading-[1.04] tracking-[-0.02em] text-balance mb-7"
            style={{ fontWeight: 380 }}
          >
            {a.headlineA}
            <br />
            <em className="italic text-[var(--terracotta)]">{a.headlineEm}</em>
            {a.headlineB}
          </h2>
          <p className="text-[18px] leading-[1.6] text-[var(--ink-soft)] mb-4">{a.leadA}</p>
          <p className="text-[17px] leading-[1.65] text-[var(--ink-soft)] mb-4">{a.leadB}</p>
          <p className="text-[17px] leading-[1.65] text-[var(--ink-soft)] mb-7">{a.leadC}</p>
          <p className="font-display italic text-[18px] leading-[1.55] text-[var(--terracotta)] flex items-start gap-2.5 mb-7">
            <span className="inline-block w-1.5 h-1.5 mt-2.5 rounded-full bg-[var(--terracotta)] animate-pulse flex-shrink-0" />
            <span>{a.soft}</span>
          </p>
        </div>

        {/* Right: animated stack of three cards. Top card holds the active
            entry, with two ghost cards staggered behind it for depth. */}
        <div className="relative min-h-[480px] md:min-h-[520px]">
          {/* Bottom ghost (offset +2) */}
          <article
            aria-hidden
            className="absolute top-[120px] left-[80px] right-0 rounded-[18px] border border-[var(--hair)] bg-[var(--paper)] p-7 opacity-40 shadow-[0_2px_4px_rgba(26,24,21,0.04)]"
            style={{ zIndex: 1 }}
          >
            <div className="font-mono text-[11px] tracking-[0.14em] uppercase text-[var(--ink-soft)] mb-3">
              {at(2).date}
            </div>
            <p className="text-[13px] leading-[1.6] text-[var(--ink-soft)] line-clamp-2">
              {at(2).quote ?? at(2).preview}
            </p>
          </article>

          {/* AI hint */}
          <div className="absolute top-[60px] right-2 flex items-center gap-2 z-[5] pointer-events-none">
            <span className="w-8 h-px bg-[var(--terracotta)] opacity-50" />
            <span className="font-display italic text-[13px] text-[var(--terracotta)] opacity-80 whitespace-nowrap">
              {a.hint}
            </span>
            <span className="w-8 h-px bg-[var(--terracotta)] opacity-50" />
          </div>

          {/* Middle ghost (offset +1) — animates between picks too */}
          <AnimatePresence mode="popLayout">
            <motion.article
              key={`mid-${(activeIndex + 1) % entries.length}`}
              aria-hidden
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 0.72, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
              className="absolute top-[68px] left-[36px] right-[44px] rounded-[20px] border border-[var(--hair)] bg-[var(--paper)] p-8 shadow-[0_8px_24px_rgba(26,24,21,0.06)]"
              style={{ zIndex: 2 }}
            >
              <div className="font-mono text-[11px] tracking-[0.14em] uppercase text-[var(--ink-soft)] mb-3">
                {at(1).date}
              </div>
              <p className="font-display italic text-[15px] leading-[1.55] text-[var(--ink-soft)] mb-3 line-clamp-2">
                &ldquo;{at(1).quote ?? at(1).preview}&rdquo;
              </p>
              <div className="font-mono text-[10px] tracking-[0.08em] text-[var(--ink-soft)] opacity-70">
                {at(1).meta}
              </div>
            </motion.article>
          </AnimatePresence>

          {/* Top card (active) — full opacity, full quote + preview + meta */}
          <AnimatePresence mode="popLayout">
            <motion.article
              key={`top-${activeIndex}`}
              initial={{ opacity: 0, y: 24, scale: 0.985 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -32, scale: 0.985 }}
              transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
              className="relative rounded-[22px] border border-[var(--hair)] bg-[var(--paper)] p-9 shadow-[0_24px_60px_rgba(26,24,21,0.1)]"
              style={{ zIndex: 3 }}
            >
              <div className="flex items-center justify-between mb-5">
                <div className="font-mono text-[11px] tracking-[0.14em] uppercase text-[var(--ink-soft)]">
                  {at(0).date}
                </div>
                <span className="inline-flex items-center gap-1.5 font-mono text-[10px] tracking-[0.12em] uppercase text-[var(--ink-soft)] opacity-70">
                  <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="0.9">
                    <path d="M3 4.5V3a2 2 0 014 0v1.5" />
                    <rect x="2" y="4.5" width="6" height="4" rx="0.5" />
                  </svg>
                  {a.topClosed}
                </span>
              </div>
              {at(0).quote && (
                <p className="font-display italic text-[19px] leading-[1.5] text-[var(--terracotta)] mb-5 flex gap-2.5 items-start">
                  <svg className="flex-shrink-0 mt-2" width="14" height="14" viewBox="0 0 14 14" fill="currentColor" aria-hidden>
                    <path d="M7 0.5l1.6 4.4 4.6.2-3.7 2.8 1.3 4.5L7 9.6 3.2 12.4l1.3-4.5L0.8 5.1l4.6-.2L7 0.5z" />
                  </svg>
                  <span>&ldquo;{at(0).quote}&rdquo;</span>
                </p>
              )}
              <p className="text-[15px] leading-[1.7] text-[var(--ink-soft)] mb-5">{at(0).preview}</p>
              <div className="font-mono text-[11px] tracking-[0.08em] text-[var(--ink-soft)] pt-4 border-t border-[var(--hair)]">
                {at(0).meta}
              </div>
            </motion.article>
          </AnimatePresence>

          {/* Pagination dots — terracotta active. */}
          <div className="absolute -bottom-2 left-0 right-0 flex justify-center gap-2 z-[4]">
            {entries.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setActiveIndex(i)}
                aria-label={`Show entry ${i + 1}`}
                className={
                  "h-1.5 rounded-full transition-all duration-300 " +
                  (i === activeIndex
                    ? "w-6 bg-[var(--terracotta)]"
                    : "w-1.5 bg-[var(--rule)] hover:bg-[var(--ink-soft)]")
                }
              />
            ))}
          </div>
        </div>
      </div>

      <p className="font-display italic text-center text-[18px] md:text-[20px] text-[var(--ink-soft)] max-w-[680px] mx-auto mt-20">
        {bridge}
      </p>
    </section>
  );
}
